// Attestation queue — wins pending for me as the named manager.
// Verify writes +50 XP to the pilot; reject requires a manager_note.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Win } from "../lib/api";

export function Attest() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<Win[]>({
    queryKey: ["attestations", "pending"],
    queryFn: api.wins.pendingForMe,
  });

  if (isLoading) return <p>Loading…</p>;
  const pending = data ?? [];

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Pending attestations</h1>
      <p className="text-sm text-slate-600">
        Wins logged by pilots who named you as the verifier. Resolve within 7
        days (the platform escalates at 14).
      </p>
      {pending.length === 0 ? (
        <p className="text-slate-600 text-sm">Nothing pending. ✓</p>
      ) : (
        <ul className="space-y-3">
          {pending.map((w) => (
            <AttestCard key={w.id} win={w} onResolved={() => qc.invalidateQueries({ queryKey: ["attestations","pending"] })} />
          ))}
        </ul>
      )}
    </div>
  );
}

function AttestCard({ win, onResolved }: { win: Win; onResolved: () => void }) {
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const verify = useMutation({
    mutationFn: (decision: "verified" | "rejected") =>
      api.wins.attest(win.id, decision, note),
    onSuccess: onResolved,
    onError: (e: Error) => setErr(e.message),
  });

  const age = ageInDays(win.submitted_at);

  return (
    <li className="border rounded p-3 bg-white space-y-2">
      <div className="flex items-baseline justify-between">
        <div className="font-medium">{win.user_name}</div>
        <div className="text-xs text-slate-500">
          {age}d ago · {win.tier_used}
          {win.high_impact ? " · high-impact" : null}
        </div>
      </div>
      <p className="text-sm">{win.outcome}</p>
      <p className="text-xs text-slate-600">Estimated hours saved: {win.est_hours_saved}</p>

      <div className="flex items-end gap-2">
        <textarea
          rows={2}
          placeholder="Manager note (required if rejecting)"
          className="flex-1 border rounded px-2 py-1 text-sm"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <div className="flex flex-col gap-1">
          <button
            disabled={verify.isPending}
            onClick={() => verify.mutate("verified")}
            className="px-3 py-1 rounded bg-emerald-600 text-white text-sm disabled:opacity-50"
          >
            Verify (+50 XP)
          </button>
          <button
            disabled={verify.isPending || note.trim() === ""}
            onClick={() => verify.mutate("rejected")}
            className="px-3 py-1 rounded bg-rose-600 text-white text-sm disabled:opacity-50"
            title={note.trim() === "" ? "Manager note required to reject" : ""}
          >
            Reject
          </button>
        </div>
      </div>
      {err ? <p className="text-xs text-red-700">{err}</p> : null}
    </li>
  );
}

function ageInDays(iso: string): number {
  const ms = Date.now() - new Date(iso).getTime();
  return Math.floor(ms / (24 * 3600_000));
}
